import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import bancoService, {
  SucursalBanco,
  SUCURSAL_BANCO_CALLE_MAX,
  SUCURSAL_BANCO_NOMBRE_MAX,
} from '../../services/bancoService';
import localidadService from '../../services/localidadService';
import LocalidadSearch from '../LocalidadSearch';
import {
  CHEQUE_NRO_SUCURSAL_MAX_DIGITOS,
  esNroSucursalChequeValido,
  mensajeErrorNroSucursalCheque,
} from '../../utils/chequeTerceroFormats';

interface SucursalesBancoSectionProps {
  nroBanco: number;
}

interface SucursalFormState {
  nroSucursal: string;
  sucursal: string;
  calle: string;
  codigoCiudad: number | null;
  nombreCiudad: string;
}

const formVacio: SucursalFormState = {
  nroSucursal: '',
  sucursal: '',
  calle: '',
  codigoCiudad: null,
  nombreCiudad: '',
};

const mensajeErrorApi = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  const msg = typeof data === 'string' ? data : data?.message || data?.title;
  if (msg) return `${fallback}: ${msg}`;
  if (error?.response?.status === 404) return `${fallback}: la sucursal no existe.`;
  return fallback;
};

const SucursalesBancoSection: React.FC<SucursalesBancoSectionProps> = ({ nroBanco }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [sucursales, setSucursales] = useState<SucursalBanco[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState<SucursalBanco | null>(null);
  const [form, setForm] = useState<SucursalFormState>({ ...formVacio });
  const [saving, setSaving] = useState(false);
  const [localidadSearchOpen, setLocalidadSearchOpen] = useState(false);

  const cargarSucursales = useCallback(async () => {
    if (!nroBanco) return;
    try {
      setLoading(true);
      const data = await bancoService.getSucursales(nroBanco);
      setSucursales([...data].sort((a, b) => a.nroSucursal - b.nroSucursal));
    } catch (error) {
      setSucursales([]);
      enqueueSnackbar(mensajeErrorApi(error, 'Error al cargar las sucursales del banco'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [nroBanco, enqueueSnackbar]);

  useEffect(() => {
    cargarSucursales();
  }, [cargarSucursales]);

  const sucursalesFiltradas = useMemo(() => {
    const termino = filtro.trim().toLowerCase();
    if (!termino) return sucursales;
    return sucursales.filter(
      (s) =>
        String(s.nroSucursal).includes(termino) ||
        (s.sucursal || '').toLowerCase().includes(termino) ||
        (s.calle || '').toLowerCase().includes(termino)
    );
  }, [sucursales, filtro]);

  const sinNombreCount = useMemo(
    () => sucursales.filter((s) => !(s.sucursal || '').trim()).length,
    [sucursales]
  );

  const abrirAlta = () => {
    setEditando(null);
    setForm({ ...formVacio });
    setDialogOpen(true);
  };

  const abrirEdicion = async (sucursal: SucursalBanco) => {
    setEditando(sucursal);
    setForm({
      nroSucursal: String(sucursal.nroSucursal),
      sucursal: (sucursal.sucursal || '').trim(),
      calle: (sucursal.calle || '').trim(),
      codigoCiudad: sucursal.codigoCiudad ?? null,
      nombreCiudad: '',
    });
    setDialogOpen(true);
    if (sucursal.codigoCiudad) {
      try {
        const localidad = await localidadService.getLocalidad(sucursal.codigoCiudad);
        const nombreCiudad = (localidad?.nombreCiudad || '').trim();
        setForm((prev) =>
          prev.codigoCiudad === sucursal.codigoCiudad ? { ...prev, nombreCiudad } : prev
        );
      } catch {
        // Sin nombre de ciudad se muestra solo el código.
      }
    }
  };

  const cerrarDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const errorNroSucursal = form.nroSucursal ? mensajeErrorNroSucursalCheque(form.nroSucursal) : null;
  const nroSucursalDuplicado =
    !editando &&
    esNroSucursalChequeValido(form.nroSucursal) &&
    sucursales.some((s) => s.nroSucursal === Number(form.nroSucursal));

  const handleGuardar = async () => {
    const nombre = form.sucursal.trim();
    if (!esNroSucursalChequeValido(form.nroSucursal)) {
      enqueueSnackbar(mensajeErrorNroSucursalCheque(form.nroSucursal) || 'N° de sucursal inválido', { variant: 'warning' });
      return;
    }
    if (nroSucursalDuplicado) {
      enqueueSnackbar(`La sucursal ${form.nroSucursal} ya existe para este banco.`, { variant: 'warning' });
      return;
    }
    if (!nombre) {
      enqueueSnackbar('Ingresá el nombre de la sucursal', { variant: 'warning' });
      return;
    }

    const datos = {
      sucursal: nombre,
      calle: form.calle.trim() || null,
      codigoCiudad: form.codigoCiudad || null,
    };

    try {
      setSaving(true);
      if (editando) {
        await bancoService.updateSucursal(nroBanco, editando.nroSucursal, datos);
      } else {
        await bancoService.createSucursal({ nroBanco, nroSucursal: Number(form.nroSucursal), ...datos });
      }
      enqueueSnackbar(editando ? 'Sucursal actualizada' : 'Sucursal creada', { variant: 'success' });
      setDialogOpen(false);
      await cargarSucursales();
    } catch (error) {
      enqueueSnackbar(mensajeErrorApi(error, 'Error al guardar la sucursal'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (sucursal: SucursalBanco) => {
    if (!window.confirm(`¿Está seguro de eliminar la sucursal ${sucursal.nroSucursal} - ${(sucursal.sucursal || '').trim()}?`)) {
      return;
    }
    try {
      await bancoService.deleteSucursal(nroBanco, sucursal.nroSucursal);
      enqueueSnackbar('Sucursal eliminada', { variant: 'success' });
      await cargarSucursales();
    } catch (error) {
      enqueueSnackbar(mensajeErrorApi(error, 'Error al eliminar la sucursal'), { variant: 'error' });
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
            Sucursales
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sucursales del banco usadas en la carga de cheques.
            {sinNombreCount > 0 &&
              ` ${sinNombreCount} ${sinNombreCount === 1 ? 'sucursal sin nombre' : 'sucursales sin nombre'} (creadas desde la carga de cheques).`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={abrirAlta}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Nueva sucursal
        </Button>
      </Stack>

      <TextField
        fullWidth
        size="small"
        placeholder="Buscar por número, nombre o calle"
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 110 }}>N° Sucursal</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Calle</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 120 }}>Cód. ciudad</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 110 }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : sucursalesFiltradas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {sucursales.length === 0 ? 'El banco no tiene sucursales cargadas.' : 'No hay sucursales que coincidan con la búsqueda.'}
                </TableCell>
              </TableRow>
            ) : (
              sucursalesFiltradas.map((s) => (
                <TableRow key={s.nroSucursal} hover>
                  <TableCell>{s.nroSucursal}</TableCell>
                  <TableCell>
                    {(s.sucursal || '').trim() || (
                      <Chip label="Sin nombre" size="small" color="warning" variant="outlined" onClick={() => abrirEdicion(s)} />
                    )}
                  </TableCell>
                  <TableCell>{(s.calle || '').trim() || '-'}</TableCell>
                  <TableCell>{s.codigoCiudad || '-'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" color="primary" onClick={() => abrirEdicion(s)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => handleEliminar(s)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={cerrarDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar sucursal' : 'Nueva sucursal'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                autoFocus={!editando}
                label="N° Sucursal"
                value={form.nroSucursal}
                disabled={!!editando}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, CHEQUE_NRO_SUCURSAL_MAX_DIGITOS);
                  setForm((prev) => ({ ...prev, nroSucursal: digits }));
                }}
                error={Boolean(errorNroSucursal) || nroSucursalDuplicado}
                helperText={
                  nroSucursalDuplicado
                    ? 'Ya existe para este banco'
                    : errorNroSucursal ?? `${CHEQUE_NRO_SUCURSAL_MAX_DIGITOS} dígitos`
                }
                inputProps={{ inputMode: 'numeric', maxLength: CHEQUE_NRO_SUCURSAL_MAX_DIGITOS }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                required
                autoFocus={!!editando}
                label="Nombre"
                value={form.sucursal}
                onChange={(e) => setForm((prev) => ({ ...prev, sucursal: e.target.value.slice(0, SUCURSAL_BANCO_NOMBRE_MAX) }))}
                helperText={`${form.sucursal.length}/${SUCURSAL_BANCO_NOMBRE_MAX}`}
                inputProps={{ maxLength: SUCURSAL_BANCO_NOMBRE_MAX }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Calle"
                value={form.calle}
                onChange={(e) => setForm((prev) => ({ ...prev, calle: e.target.value.slice(0, SUCURSAL_BANCO_CALLE_MAX) }))}
                helperText={`${form.calle.length}/${SUCURSAL_BANCO_CALLE_MAX}`}
                inputProps={{ maxLength: SUCURSAL_BANCO_CALLE_MAX }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Localidad"
                value={
                  form.codigoCiudad
                    ? `${form.codigoCiudad}${form.nombreCiudad ? ` - ${form.nombreCiudad}` : ''}`
                    : ''
                }
                placeholder="Buscar localidad"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      {form.codigoCiudad ? (
                        <IconButton
                          size="small"
                          aria-label="Quitar localidad"
                          onClick={() => setForm((prev) => ({ ...prev, codigoCiudad: null, nombreCiudad: '' }))}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                      <IconButton size="small" aria-label="Buscar localidad" onClick={() => setLocalidadSearchOpen(true)}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={cerrarDialog} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleGuardar}
            disabled={saving}
            sx={{ textTransform: 'none' }}
            startIcon={saving ? <CircularProgress size={18} /> : null}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <LocalidadSearch
        open={localidadSearchOpen}
        onClose={() => setLocalidadSearchOpen(false)}
        onSelect={(localidad) =>
          setForm((prev) => ({
            ...prev,
            codigoCiudad: localidad.codigoCiudad,
            nombreCiudad: (localidad.nombreCiudad || '').trim(),
          }))
        }
      />
    </Box>
  );
};

export default SucursalesBancoSection;
